import { Box, Grid } from '@mui/material'
import { useIntl } from 'react-intl'
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem'
import { LabelItem } from '../../../components/Typographies/LabelItem'
import SideModalWrapper from '../../Assets/modals/SideModalWrapper'
import { messages } from '../messages'
import { UnassignedLoadContents } from '../types'

type ContentDetailsSidePanelProps = {
  open: boolean
  onClose: () => void
  content: UnassignedLoadContents | null
  countryName: string
}

const ContentDetailsSidePanel = ({
  open,
  onClose,
  content,
  countryName,
}: ContentDetailsSidePanelProps) => {
  const { formatMessage } = useIntl()

  if (!content) return null

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
      return formatMessage(messages.notAvailable)
    }
    return value.toFixed(2)
  }

  const formatDecimal = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
      return formatMessage(messages.notAvailable)
    }
    return value.toFixed(2)
  }

  const hasPhysicalProperties = content.netWeight !== null && content.netWeight !== undefined ||
    content.grossWeight !== null && content.grossWeight !== undefined ||
    content.volume !== null && content.volume !== undefined ||
    content.height !== null && content.height !== undefined ||
    content.length !== null && content.length !== undefined ||
    content.width !== null && content.width !== undefined

  const hasWidth = content.width !== null && content.width !== undefined
  const lengthLabel = hasWidth ? messages.length : messages.diameter

  const hasTemperatureSettings = content.setPoint !== null && content.setPoint !== undefined

  const hasHsCodes = content.hsHeadingId !== null && content.hsHeadingId !== undefined ||
    content.hsSubheadingId !== null && content.hsSubheadingId !== undefined

  const sectionBoxStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    border: '1px solid #ccc',
    borderLeft: '4px solid #636363',
    backgroundColor: '#ffffff',
    borderRadius: '4px',
    padding: '16px',
    gap: '8px',
    marginBottom: '16px',
  }

  return (
    <SideModalWrapper
      open={open}
      onClose={onClose}
      headerLabel={formatMessage(messages.contentDetails)}
      withBottomBox={false}
      width="50%"
    >
      <Box sx={{ p: '32px' }}>
        {/* Content Information Section */}
        <Box sx={sectionBoxStyle}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <LabelItem>{formatMessage(messages.contentInformation)}</LabelItem>
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoDisplayItem
                label={formatMessage(messages.productDescription)}
                value={content.description ?? formatMessage(messages.notAvailable)}
                isSmallScreen={false}
                allowOverlap={true}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoDisplayItem
                label={formatMessage(messages.countryOfOrigin)}
                value={countryName}
                isSmallScreen={false}
                allowOverlap={true}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoDisplayItem
                label={formatMessage(messages.quantity)}
                value={content.quantity?.toString() ?? formatMessage(messages.notAvailable)}
                isSmallScreen={false}
                allowOverlap={true}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoDisplayItem
                label={formatMessage(messages.unitPrice)}
                value={formatCurrency(content.unitPrice)}
                isSmallScreen={false}
                allowOverlap={true}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoDisplayItem
                label={formatMessage(messages.total)}
                value={formatCurrency(content.total)}
                isSmallScreen={false}
                allowOverlap={true}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Physical Properties Section */}
        {hasPhysicalProperties && (
          <Box sx={sectionBoxStyle}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <LabelItem>{formatMessage(messages.physicalProperties)}</LabelItem>
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(messages.netWeight)}
                  value={formatDecimal(content.netWeight)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(messages.grossWeight)}
                  value={formatDecimal(content.grossWeight)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(messages.volume)}
                  value={formatDecimal(content.volume)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(lengthLabel)}
                  value={formatDecimal(content.length)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
              {hasWidth && (
                <Grid item xs={12} md={4}>
                  <InfoDisplayItem
                    label={formatMessage(messages.width)}
                    value={formatDecimal(content.width)}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(messages.height)}
                  value={formatDecimal(content.height)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* HS Codes Section */}
        {hasHsCodes && (
          <Box sx={sectionBoxStyle}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <LabelItem>{formatMessage(messages.hsCodes)}</LabelItem>
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoDisplayItem
                  label={formatMessage(messages.hsHeading)}
                  value={content.hsHeadingId?.toString() ?? formatMessage(messages.notAvailable)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoDisplayItem
                  label={formatMessage(messages.hsSubheading)}
                  value={content.hsSubheadingId?.toString() ?? formatMessage(messages.notAvailable)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Temperature Settings Section */}
        {hasTemperatureSettings && (
          <Box sx={sectionBoxStyle}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <LabelItem>{formatMessage(messages.temperatureSettings)}</LabelItem>
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(messages.setPoint)}
                  value={formatDecimal(content.setPoint)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(messages.upperThresholdWarning)}
                  value={formatDecimal(content.upperThresholdWarning)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(messages.upperThresholdCritical)}
                  value={formatDecimal(content.upperThresholdCritical)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(messages.lowerThresholdWarning)}
                  value={formatDecimal(content.lowerThresholdWarning)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InfoDisplayItem
                  label={formatMessage(messages.lowerThresholdCritical)}
                  value={formatDecimal(content.lowerThresholdCritical)}
                  isSmallScreen={false}
                  allowOverlap={true}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </SideModalWrapper>
  )
}

export default ContentDetailsSidePanel
